import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import OpenTableEmbed from "../components/OpenTableEmbed";

const TYPES = [
  { id: "restaurant", label: "Restaurant" },
  { id: "bar", label: "Bar" },
  { id: "cigar-lounge", label: "Cigar Lounge" },
];

export default function Reservations() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", party_size: 2, date: "", time: "",
    type: params.get("type") || "restaurant", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/reservations", { ...form, party_size: parseInt(form.party_size) || 2 });
      setDone(true);
      toast.success("Your request is being processed.");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="reservations-page">
      <section className="pt-40 pb-12 px-6 sm:px-10 lg:px-16">
        <div className="max-w-[1600px] mx-auto">
          <p className="eyebrow">Reservations</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight">
            Hold the <span className="italic" style={{ color: "var(--twr-bronze)" }}>room.</span>
          </h1>
          <p className="text-[var(--twr-warm-white)]/70 mt-6 max-w-2xl">
            Submit your request below. A member of our team will personally confirm by phone or email — typically within
            two business hours.
          </p>
        </div>
      </section>

      <section className="section pt-8">
        <div className="max-w-[1100px] mx-auto grid lg:grid-cols-5 gap-10">
          <OpenTableEmbed>
            {() => (
              !done ? (
            <form onSubmit={submit} data-testid="reservation-form" className="surface p-8 sm:p-12 lg:col-span-3">
              <h2 className="font-serif text-2xl">Submit a request</h2>

              <div className="mt-8">
                <p className="eyebrow mb-3">Where</p>
                <div className="flex flex-wrap gap-2">
                  {TYPES.map((t) => (
                    <button key={t.id} type="button" onClick={() => set("type", t.id)}
                      data-testid={`reservation-type-${t.id}`}
                      className={`pill ${form.type === t.id ? "active" : ""}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                <Field label="Name" v={form.name} on={(v) => set("name", v)} required testId="reservation-name" />
                <Field label="Email" type="email" v={form.email} on={(v) => set("email", v)} required testId="reservation-email" />
                <Field label="Phone" v={form.phone} on={(v) => set("phone", v)} required testId="reservation-phone" />
                <Field label="Party Size" type="number" v={form.party_size} on={(v) => set("party_size", v)} required testId="reservation-party" />
                <Field label="Date" type="date" v={form.date} on={(v) => set("date", v)} required testId="reservation-date" />
                <Field label="Time" type="time" v={form.time} on={(v) => set("time", v)} required testId="reservation-time" />
              </div>

              <label className="block mt-5">
                <span className="eyebrow">Notes (allergies, occasion, seating preference)</span>
                <textarea data-testid="reservation-notes" rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-base" />
              </label>

              <button disabled={loading} data-testid="reservation-submit" className="btn-bronze mt-8 w-full sm:w-auto">
                {loading ? "Submitting…" : "Submit Reservation"}
              </button>
              <p className="text-[var(--twr-warm-white)]/45 text-[12px] mt-4">
                Requests are personally confirmed by our concierge.
              </p>
            </form>
          ) : (
            <div className="surface p-12 text-center lg:col-span-3" data-testid="reservation-thankyou">
              <div className="monogram mx-auto" style={{ width: 60, height: 60, fontSize: 14 }}>TWR</div>
              <p className="eyebrow mt-6">Reservation Requested</p>
              <h2 className="font-serif text-3xl mt-4">We've got it.</h2>
              <p className="text-[var(--twr-warm-white)]/70 mt-4 leading-relaxed">A member of our team will confirm shortly. For urgent requests, call <a href="tel:+14075550100" className="text-[var(--twr-bronze)]">(407) 555-0100</a>.</p>
            </div>
          )
            )}
          </OpenTableEmbed>

          <aside className="lg:col-span-2 space-y-6">
            <div className="surface p-6">
              <p className="eyebrow">Hours</p>
              <ul className="mt-4 space-y-2 text-sm text-[var(--twr-warm-white)]/75">
                <li className="flex items-center gap-2"><Clock size={13} className="text-[var(--twr-bronze)]"/> Mon — Thu · 11:30 — 23:00</li>
                <li className="flex items-center gap-2"><Clock size={13} className="text-[var(--twr-bronze)]"/> Fri — Sat · 11:30 — 00:00</li>
                <li className="flex items-center gap-2"><Clock size={13} className="text-[var(--twr-bronze)]"/> Sunday · 10:00 — 22:00</li>
              </ul>
            </div>
            <div className="surface p-6">
              <p className="eyebrow">Concierge</p>
              <p className="text-sm mt-3 text-[var(--twr-warm-white)]/75 leading-relaxed">
                For larger parties, private dining, or member reservations — call our concierge directly at <a href="tel:+14075550100" className="text-[var(--twr-bronze)]">(407) 555-0100</a>.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, v, on, type = "text", required, testId }) {
  return (
    <label className="block">
      <span className="eyebrow">{label}{required && " *"}</span>
      <input data-testid={testId} type={type} value={v} onChange={(e) => on(e.target.value)} required={required}
        className="mt-3 w-full bg-transparent border border-[var(--twr-line-strong)] focus:border-[var(--twr-bronze)] outline-none px-4 py-3 text-sm transition-colors" />
    </label>
  );
}
